import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAtom } from "jotai";
import { useToasts } from "react-toast-notifications";
import YouTube, { Options as YouTubeOptions } from "react-youtube";
import chroma from "chroma-js";
import {
  Block,
  YoutubeBlock as YoutubeBlockType,
  YoutubeBlockViewState,
  YTPlayerState,
} from "../types";

import Icon from "../components/Icon";
import { Slider } from "../components/Slider";
import { currentUserAtom } from "../jotai/atoms";
import { useUserInfo } from "../liveblocks/maps";
import { keyframes, styled } from "../stitches.config";
import { User } from "../types";
import { resolveUserColor } from "../css/color";

interface BlockViewProps<T extends Block> {
  block: T;
  updateBlock: (block: Partial<T>, raise?: boolean) => void;
  deleteBlock: (blockId: string) => void;
}

interface YouTubePlayer {
  getDuration: () => Promise<number>;
  getCurrentTime: () => Promise<number>;
  seekTo: (seconds: number, allowSeekAhead: boolean) => Promise<void>;
  setPlaybackRate: (suggestedRate: number) => Promise<void>;
  stopVideo: () => Promise<void>;
  pauseVideo: () => Promise<void>;
  playVideo: () => Promise<void>;
  /** Accepts an integer between 0 and 100. */
  setVolume: (volume: number) => Promise<void>;
  getVolume: () => Promise<number>;
  mute: () => Promise<void>;
  unMute: () => Promise<void>;
}

interface YouTubeEvent<T> {
  target: YouTubePlayer;
  data: T;
}

type YouTubeStateChangeEvent = YouTubeEvent<number>;
type YouTubePlaybackRateChangeEvent = YouTubeEvent<number>;

const youtubeOptions: YouTubeOptions = {
  width: "100%",
  height: "100%",
  playerVars: {
    modestbranding: 1,
    controls: 0,
  },
};
const playbackTimeTolerance = 1.5; // seconds
const initialVolume = 20; // valid range: 0-100

function YoutubeBlock(props: BlockViewProps<YoutubeBlockType>) {
  const { block } = props;
  const { viewState: state } = block;

  switch (state) {
    case YoutubeBlockViewState.PlayVideo:
      return <PlayVideoView {...props} />;
    case YoutubeBlockViewState.UrlInput:
    default:
      return <UrlInputView {...props} />;
  }
}

function UrlInputView({
  block,
  updateBlock,
}: BlockViewProps<YoutubeBlockType>) {
  const url = block.value || "";
  const { addToast } = useToasts();

  const handleUrlChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      updateBlock({ value: e.target.value }, false);
    },
    [updateBlock]
  );

  const tryPlayVideo = useCallback(() => {
    const videoId = getVideoId(url);
    if (videoId) {
      updateBlock(
        {
          value: url,
          viewState: YoutubeBlockViewState.PlayVideo,
          playerState: YTPlayerState.Playing,
          playbackTime: 0,
          playbackSpeed: 1,
          playerUpdatedAt: Date.now(),
          isBeingHovered: false,
        },
        false
      );
    } else {
      addToast("We couldn't find a video from this URL");
    }
  }, [updateBlock, addToast, url]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case "Enter": {
          if (e.altKey || e.ctrlKey || e.metaKey) break;
          tryPlayVideo();
          break;
        }
      }
    },
    [tryPlayVideo]
  );

  return (
    <UrlInputContainer>
      <UrlInput
        type="url"
        onChange={handleUrlChange}
        onKeyDown={handleKeyDown}
        onMouseDown={(e) => e.stopPropagation()} // prevent moving/selecting block
        value={url}
        data-cursor={"text"}
        placeholder="Enter YouTube URL"
      />
      <HeadingIconContainer>
        <HeadingIcon icon="YouTube" />
      </HeadingIconContainer>
    </UrlInputContainer>
  );
}

function PlayVideoView({
  block,
  updateBlock,
}: BlockViewProps<YoutubeBlockType>) {
  const videoId = getVideoId(block.value || "");

  /** This should not happen since url is checked before allowed to play. */
  if (!videoId) {
    /** Redirect to UrlInput state in case it happens. */
    updateBlock({ viewState: YoutubeBlockViewState.UrlInput }, false);
  }

  const [currentUser] = useAtom(currentUserAtom);

  const playerRef = useRef<YouTubePlayer>();
  const playbackTimeInputRef = useRef<HTMLInputElement>(null);

  /** Recover current playback time with some math. */
  const recoverPlaybackTime = useCallback(() => {
    return block.playerState !== YTPlayerState.Playing
      ? block.playbackTime
      : block.playbackTime +
          ((Date.now() - block.playerUpdatedAt) / 1000) * block.playbackSpeed;
  }, [
    block.playerState,
    block.playbackTime,
    block.playerUpdatedAt,
    block.playbackSpeed,
  ]);

  const [isCurrentUserHoveringBlock, setIsCurrentUserHoveringBlock] =
    useState(false);
  const [isDraggingPlaybackTime, setIsDraggingPlaybackTime] = useState(false);
  const [isPlaying, setIsPlaying] = useState(
    block.playerState === YTPlayerState.Playing
  );
  const [playbackTime, setPlaybackTime] = useState(() => recoverPlaybackTime());
  const [pointedTime, setPointedTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(initialVolume);
  const [isHoveringVolume, setIsHoveringVolume] = useState(false);

  const toggleMute = useCallback(async () => {
    const volume = (await playerRef.current?.getVolume()) || initialVolume;
    setVolume((vol) => (vol ? 0 : volume));
  }, []);

  /** Update global state for player-related actions. */
  const updateGlobalPlayerState = useCallback(
    (togglePlayPause = false, endPlayback = false, playbackSpeed = 1) => {
      if (!playerRef.current) return;
      updateBlock(
        {
          playerState: (() => {
            if (endPlayback) return YTPlayerState.Ended;
            else if (togglePlayPause) {
              return isPlaying ? YTPlayerState.Paused : YTPlayerState.Playing;
            } else {
              return isPlaying ? YTPlayerState.Playing : YTPlayerState.Paused;
            }
          })(),
          playbackTime: endPlayback ? 0 : playbackTime,
          playbackSpeed,
          playerUpdatedAt: Date.now(),
        },
        false
      );
    },
    [updateBlock, isPlaying, playbackTime]
  );

  /** Handle video ending. */
  const handleYTStateChange = useCallback(
    (event: YouTubeStateChangeEvent) => {
      const player = playerRef.current;
      if (!player) return;
      switch (event.data) {
        case 0: {
          setIsPlaying(false);
          updateGlobalPlayerState(false, true);
          return;
        }
      }
    },
    [updateGlobalPlayerState]
  );

  /** Handle playback speed change. */
  const handleYTPlaybackRateChange = useCallback(
    (event: YouTubePlaybackRateChangeEvent) => {
      const player = playerRef.current;
      if (!player) return;
      updateGlobalPlayerState(false, false, event.data);
    },
    [updateGlobalPlayerState]
  );

  /** Handle YouTube player loading ready. */
  const handleYTReady = useCallback(async () => {
    const player = playerRef.current;
    if (!player) return;
    l("ytp ready");

    /** Set duration. */
    const duration = await player.getDuration();
    setDuration(duration);

    /** Seek to time. */
    if (
      block.playerState === YTPlayerState.Paused ||
      block.playerState === YTPlayerState.Playing
    ) {
      const localPlaybackTime = await player.getCurrentTime();
      const globalPlaybackTime = recoverPlaybackTime();
      if (
        Math.abs(localPlaybackTime - globalPlaybackTime) > playbackTimeTolerance
      ) {
        player.seekTo(globalPlaybackTime, true);
        setPlaybackTime(globalPlaybackTime);
      }
    }

    /** Set playback speed. */
    player.setPlaybackRate(block.playbackSpeed);

    /** Set play/pause. */
    if (block.playerState === YTPlayerState.Playing) {
      player.playVideo();
      setIsPlaying(true);
    } else {
      player.pauseVideo();
      setIsPlaying(false);
    }
  }, [block.playerState, block.playbackSpeed, recoverPlaybackTime]);

  /** Update progress bar and timer. */
  useEffect(() => {
    const id = setInterval(async () => {
      const player = playerRef.current;
      if (
        isPlaying &&
        player &&
        !isDraggingPlaybackTime &&
        /**
         * When the range input is focused and being controlled by keyboard,
         * don't update it.
         */
        document.activeElement !== playbackTimeInputRef.current
      ) {
        const t = await player.getCurrentTime();
        setPlaybackTime(t);
      }
    }, 1000);

    return () => clearInterval(id);
  }, [isPlaying, isDraggingPlaybackTime]);

  /** Play/pause, local action. */
  const handlePlayPause = useCallback(async () => {
    if (!playerRef.current) return;
    if (isPlaying) playerRef.current.pauseVideo();
    else playerRef.current.playVideo();
    l(`local ${isPlaying ? "pause" : "play"}`);
    setIsPlaying(!isPlaying);
    updateGlobalPlayerState(true);
  }, [isPlaying, updateGlobalPlayerState]);

  /** Playback time dragging, local action. */
  const handlePlaybackTimeChange = useCallback(
    (time: number) => {
      if (!playerRef.current) return;
      l(`local time dragging ${time}`);
      playerRef.current.seekTo(time, !isDraggingPlaybackTime);
      setPlaybackTime(time);
    },
    [isDraggingPlaybackTime]
  );

  /** Playback time set, local action. */
  const handlePlaybackTimeSet = useCallback(
    (time: number) => {
      if (!playerRef.current) return;
      l(`local time set ${time}`);
      playerRef.current.seekTo(time, true);
      setPlaybackTime(time);
      updateGlobalPlayerState();
    },
    [updateGlobalPlayerState]
  );

  /** Playback time, remote action. */
  useEffect(() => {
    l("remote action start -----");
    l(`remote time set ${block.playbackTime}`);

    playerRef.current?.seekTo(block.playbackTime, true);
    setPlaybackTime(block.playbackTime);

    if (block.playerState === YTPlayerState.Playing) {
      l("remote play");
      playerRef.current?.playVideo();
      setIsPlaying(true);
    } else {
      l("remote pause");
      playerRef.current?.pauseVideo();
      setIsPlaying(false);
    }

    l("remote action end -----");
  }, [block.playbackTime, block.playerState]);

  /** Volume is not synced. */
  const handleVolumeChange = useCallback((volume: number) => {
    l("vol change");
    if (!playerRef.current) return;
    setVolume(volume);
  }, []);

  useEffect(() => {
    if (volume === 0) playerRef.current?.mute();
    else {
      playerRef.current?.unMute();
      playerRef.current?.setVolume(volume);
    }
  }, [volume]);

  /** Managing hover state. */
  const onHover = useCallback(() => {
    setIsCurrentUserHoveringBlock(true);
    updateBlock({ isBeingHovered: true }, false);
  }, [updateBlock]);

  const onStopHover = useCallback(() => {
    setIsCurrentUserHoveringBlock(false);
    updateBlock({ isBeingHovered: false }, false);
  }, [updateBlock]);

  /**
   * Volume bar theme colors are hand-picked, so here's a dedicate function
   * to map them.
   */
  const themeColor = getVolumeBarColor(currentUser?.color);

  return (
    <YouTubeContainer
      onMouseOver={onHover}
      onMouseLeave={onStopHover}
      disableInteractionWithIframe={true}
    >
      <YouTube
        ref={(yt) => (playerRef.current = yt?.getInternalPlayer())}
        videoId={videoId}
        opts={youtubeOptions}
        onStateChange={handleYTStateChange}
        onPlaybackRateChange={handleYTPlaybackRateChange}
        onReady={handleYTReady}
      />
      {/** There are three layers of ControlBars, which show under different conditions. */}
      <PlayControlBar
        data-cursor={"pointer"}
        onMouseDown={(e) => e.stopPropagation()}
        show={!isPlaying}
      >
        <FeatureButton
          data-cursor={"pointer"}
          color="primary"
          onClick={handlePlayPause}
        >
          <FeatureIcon icon={isPlaying ? "Pause" : "Play"} />
        </FeatureButton>
      </PlayControlBar>
      <MainControlBar
        onMouseDown={(e) => e.stopPropagation()}
        show={isCurrentUserHoveringBlock || block.isBeingHovered}
        fade={isHoveringVolume}
      >
        <FeatureButton
          data-cursor={"pointer"}
          color="primary"
          onClick={handlePlayPause}
        >
          <FeatureIcon icon={isPlaying ? "Pause" : "Play"} />
        </FeatureButton>
        <Timer seconds={pointedTime || playbackTime} pointed={!!pointedTime} />
        <Slider
          data-cursor={"pointer"}
          ref={playbackTimeInputRef}
          min={0}
          max={duration}
          step={1}
          value={playbackTime}
          onChange={handlePlaybackTimeChange}
          onMouseDown={() => setIsDraggingPlaybackTime(true)}
          onMouseUp={(time) => {
            setIsDraggingPlaybackTime(false);
            handlePlaybackTimeSet(time);
          }}
          onPointed={(value) => setPointedTime(value)}
          showPointed={true}
        />
        <Timer seconds={duration} />
        <FeatureButton
          data-cursor={"pointer"}
          onClick={toggleMute}
          onMouseEnter={() => setIsHoveringVolume(true)}
          onMouseLeave={() => setIsHoveringVolume(false)}
        >
          <FeatureIcon icon={volume ? "Volume" : "VolumeMute"} />
        </FeatureButton>
      </MainControlBar>
      <VolumeControlBarWrapper
        show={isHoveringVolume}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseLeave={() => setIsHoveringVolume(false)}
        data-cursor={"pointer"}
      >
        <VolumeTooltip>My volume</VolumeTooltip>
        <VolumeControlBar>
          <Slider
            min={0}
            max={100}
            step={1}
            value={volume}
            onChange={handleVolumeChange}
            containerStyle={{ width: 100 }}
            themeColor={themeColor}
          />
          <FeatureButton
            onClick={toggleMute}
            onMouseEnter={() => setIsHoveringVolume(true)}
          >
            <FeatureIcon icon={volume ? "Volume" : "VolumeMute"} />
          </FeatureButton>
        </VolumeControlBar>
      </VolumeControlBarWrapper>
    </YouTubeContainer>
  );
}

/** For UrlInputView. */
const UrlInputContainer = styled("div", {
  padding: "$2",
  display: "flex",
  flexDirection: "column",
});

const UrlInput = styled("input", {
  background: "rgba(113, 81, 78, 0.05)",
  boxShadow: "inset 0px 2px 0px 0px rgba(113, 81, 78, 0.05)",
  borderRadius: "$1",
  padding: "0.375rem 0.5rem",
  fontSize: "0.875rem",
  lineHeight: 1.25,
  color: "#330a0a",
  "&::placeholder": {
    color: chroma("#330a0a").alpha(0.5).css(),
  },
  "&:hover": {
    outline: "0.5px solid rgba(184, 162, 156, 0.2)",
  },
  "&:focus": {
    border: "none",
    boxShadow: "inset 0px 2px 0px 0.5px rgba(113, 81, 78, 0.05)",
    outline: "1px solid #B8A29C",
  },
});

const HeadingIconContainer = styled("div", {
  flex: "1 1 0",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const HeadingIcon = styled(Icon, {
  width: 64,
  height: 64,
  fill: "#330a0a",
  opacity: 0.05,
});

/** For PlayVideoView. */
const YouTubeContainer = styled("div", {
  position: "relative",
  "&>div:first-child": {
    width: "100%",
    height: "100%",
    overflow: "hidden",
  },
  variants: {
    disableInteractionWithIframe: {
      true: {
        "& iframe": {
          pointerEvents: "none",
        },
      },
    },
  },
});

const fadeIn = keyframes({
  "0%": { opacity: 0, visibility: "visible" },
  "100%": { opacity: 1 },
});

const fadeOut = keyframes({
  "0%": { opacity: 1 },
  "100%": { opacity: 0, visibility: "hidden" },
});

/**
 * Using `animation-fill-mode: forwards` to keep state at the animation's
 * end works, but that way `backdrop-filter: blur()` in nested elements
 * doesn't work. Specifying static styles that are independent of the
 * animation solves it.
 */
const showToggle = {
  true: {
    opacity: 1,
    animation: `${fadeIn} 0.1s ease-out`,
  },
  false: {
    opacity: 0,
    visibility: "hidden",
    animation: `${fadeOut} 0.1s ease-out`,
  },
};

const ControlBar = styled("div", {
  height: "$6",
  padding: "0 $1",
  borderRadius: "$2",
  transition: "opacity 0.1s ease-out, background 0.1s ease-out",
  background: "rgba(57, 41, 40, 0.5)",
  backdropFilter: "blur(1rem)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  cursor: "none",
  variants: {
    show: showToggle,
    fade: {
      true: {
        background: "rgba(57, 41, 40, 0.25)",
        "& > *": {
          opacity: 0.25,
          transition: "opacity 0.1s ease-out",
        },
      },
    },
  },
});

const FeatureIcon = styled(Icon, {
  width: "1.5rem",
  height: "1.5rem",
});

const FeatureButton = styled("button", {
  width: "2rem",
  height: "2rem",
  borderRadius: "$1",
  background: "transparent",
  border: "none",
  color: "#fff",
  padding: "0.25rem",
  transition: "background 0.1s ease-out",
  "&:hover": {
    background: "rgba(184, 162, 156, 0.1)",
  },
  "&:active": {
    background: "rgba(184, 162, 156, 0.2)",
  },
});

const TimerContainer = styled("div", {
  flex: "0 0 2.375rem",
  color: "rgba(255, 255, 255, 0.35)",
  fontSize: "0.625rem",
  lineHeight: 1.2,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  transition: "color 0.1s ease-out",
  /** Fine-tune visual feeling. */
  paddingTop: "0.04rem",
  variants: {
    pointed: {
      true: {
        color: "#ffffff",
      },
    },
  },
});

const PlayControlBar = styled(ControlBar, {
  position: "absolute",
  bottom: "$2",
  left: "$2",
});

const MainControlBar = styled(ControlBar, {
  position: "absolute",
  bottom: "$2",
  left: "$2",
  right: "$2",
});

const VolumeControlBar = styled(ControlBar, {
  padding: "0 $1 0 $2",
});

const VolumeControlBarWrapper = styled("div", {
  position: "absolute",
  right: 0,
  bottom: 0,
  padding: "$2",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  variants: {
    show: showToggle,
  },
});

const VolumeTooltip = styled("label", {
  borderRadius: "$1",
  color: "#ffffff",
  background: "rgba(57, 41, 40, 0.8)",
  padding: "$1 calc($1 * 1.5)",
  fontSize: "0.75rem",
  lineHeight: "1rem",
  marginBottom: "calc($1 * 0.5)",
});

export default YoutubeBlock;

function Timer({ seconds, pointed }: { seconds: number; pointed?: boolean }) {
  const hh = Math.floor(seconds / 3600);
  const mm = Math.floor((seconds % 3600) / 60);
  const ss = Math.floor((seconds % 3600) % 60);

  function padZeros(n: number) {
    return n.toString().padStart(2, "0");
  }

  return (
    <TimerContainer pointed={pointed}>{`${hh ? `${hh}:` : ""}${
      hh ? padZeros(mm) : mm
    }:${padZeros(ss)}`}</TimerContainer>
  );
}

function getVideoId(url: string | undefined): string | undefined {
  if (!url || !url.startsWith("https://www.youtube.com/")) return undefined;
  const resolvedUrl = new URL(url);
  const videoId = resolvedUrl.searchParams.get("v");
  if (!videoId) return undefined;
  return videoId;
}

/** Map userColor to volume bar theme color. */
export function getVolumeBarColor(
  userColor: User["color"] | undefined
): string | undefined {
  const color = userColor ? resolveUserColor(userColor) : null;

  switch (color) {
    case "yellow": {
      return "#ECD87E";
    }
    case "pink": {
      return "#FFB1C6";
    }
    case "red": {
      return "#FFB3AB";
    }
    case "blue": {
      return "#87D4F7";
    }
    case "purple": {
      return "#CCC1FD";
    }
    case "orange": {
      return "#FCBE83";
    }
    case "green": {
      return "#8FDB9E";
    }
  }
}

function l(msg: string) {
  console.log("youtube:", msg);
}
